import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Network, Gauge, Ship, FileCheck2, Globe2 } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";
import CompanyProfile from "@/components/CompanyProfile";
import UnixxStrip from "@/components/UnixxStrip";
import { COMPANY } from "@/constants/company";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: EASE },
});

const STATS = [
  { value: "10", suffix: "+", label: "Years of trading experience" },
  { value: "100,000", suffix: "+", label: "Metric tonnes facilitated" },
  { value: "3", suffix: "", label: "Core product lines" },
  { value: "15", suffix: "+", label: "Markets served" },
];

const BUSINESSES = [
  {
    title: "Bitumen",
    image: "/assets/bitutainer.jpg",
    to: "/bitumen",
    testid: "core-card-bitumen",
    points: ["Penetration grades 60/70 & 80/100", "Cutback MC-30 / MC-70", "Emulsions on request"],
  },
  {
    title: "Lubricants",
    image: "/assets/lubricants.jpeg",
    to: "/lubricants",
    testid: "core-card-lubricants",
    points: ["Automotive & industrial oils", "Marine lubricants", "Base oils SN150 / SN500 / BS150"],
  },
  {
    title: "Petrochemicals",
    image: "/assets/chem1.webp",
    to: "/petrochemicals",
    testid: "core-card-petrochemicals",
    points: ["Aromatics & solvents", "Chemical intermediates", "ISO tank supply chain"],
  },
];

const WHY = [
  {
    icon: Network,
    title: "Strong supplier network",
    text: "Direct relationships with refineries and producers keep supply consistent, even when markets tighten.",
  },
  {
    icon: ShieldCheck,
    title: "Quality without compromise",
    text: "Every cargo is tested against international standards before it moves — specifications are verified, not assumed.",
  },
  {
    icon: Gauge,
    title: "Competitive sourcing",
    text: "Multi-origin quoting lets us benchmark prices across regions so you buy at the right level.",
  },
  {
    icon: Ship,
    title: "Execution-first logistics",
    text: "From storage to multimodal freight, we manage the supply chain end to end so lead times stay short.",
  },
];

const STEPS = [
  { n: "01", title: "Understand requirements", text: "Specification, volume, destination and timeline." },
  { n: "02", title: "Source & quote", text: "Competitive pricing from verified suppliers." },
  { n: "03", title: "Contract & execute", text: "Secure documentation and logistics coordination." },
  { n: "04", title: "Deliver & support", text: "Shipment monitoring through to final delivery." },
];

const DOCS = ["Certificate of Analysis (COA)", "Bill of Lading (BL)", "Commercial Invoice & Packing List", "SGS / equivalent inspection"];

function MaskedLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Chapter({ no, kicker, title }) {
  return (
    <motion.div {...reveal()} className="flex flex-wrap items-end gap-x-8 gap-y-3 border-b border-navy-600/60 pb-8">
      <span className="text-outline-ghost font-heading text-6xl font-extrabold leading-none md:text-7xl">{no}</span>
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-brand">{kicker}</p>
        <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">{title}</h2>
      </div>
    </motion.div>
  );
}

function Intro() {
  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-navy"
      exit={{ y: "-100%" }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
      data-testid="intro-overlay"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="flex flex-col items-center gap-4"
      >
        <img src={COMPANY.logo} alt={`${COMPANY.name} logo`} className="h-20 w-20 rounded-full object-cover ring-2 ring-brand/50" />
        <p className="font-mono text-xs uppercase tracking-[0.45em] text-brand">Excellent Traders</p>
      </motion.div>
    </motion.div>
  );
}

function Hero({ base }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);

  return (
    <section ref={ref} className="relative flex min-h-[calc(100vh-6rem)] flex-col justify-center overflow-hidden">
      <motion.div style={{ y: yBg, opacity }} className="absolute inset-0">
        <motion.img
          src="/assets/hero-bg.png"
          alt=""
          className="h-[120%] w-full object-cover opacity-40"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, delay: base, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/80 to-navy" />

      <div className="relative mx-auto w-full max-w-7xl px-6 py-20 md:px-12">
        <motion.p {...reveal(base + 0.1)} className="font-mono text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-brand">
          Singapore — 1.3521° N, 103.8198° E · Energy &amp; Commodity Trading
        </motion.p>

        <h1
          className="mt-8 font-heading text-[10vw] font-extrabold leading-[0.92] tracking-tight text-white sm:text-6xl lg:text-7xl"
          data-testid="hero-headline"
        >
          <MaskedLine delay={base + 0.15}>PETROLEUM &amp;</MaskedLine>
          <MaskedLine delay={base + 0.28} className="text-outline">STRATEGIC</MaskedLine>
          <MaskedLine delay={base + 0.41}>
            COMMODITIES<span className="text-brand">.</span>
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-end">
          <motion.div {...reveal(base + 0.55)} className="flex flex-wrap gap-4">
            <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="inline-flex items-center gap-2 rounded-md bg-brand px-7 py-3 text-sm font-semibold text-navy transition-shadow duration-300 hover:shadow-[0_0_32px_rgba(0,208,132,0.45)]"
              >
                Request a quote <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.span>
            <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <a
                href="#core-business"
                data-testid="hero-cta-products"
                className="inline-flex items-center gap-2 rounded-md border border-navy-600 px-7 py-3 text-sm font-semibold text-slate-200 transition-colors duration-200 hover:border-brand/60 hover:text-brand"
              >
                Explore the businesses
              </a>
            </motion.span>
          </motion.div>
        </div>

        <motion.div {...reveal(base + 0.8)} className="mt-16 grid grid-cols-2 gap-6 border-t border-navy-600/60 pt-8 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <div key={s.label} data-testid={`stat-${i}`}>
              <p className="font-heading text-2xl font-bold text-white sm:text-3xl">
                {s.value}
                <span className="text-brand">{s.suffix}</span>
              </p>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const words = ["BITUMEN", "LUBRICANTS", "PETROCHEMICALS", "GLOBAL TRADING", "SINGAPORE"];
  const row = [...words, ...words];
  return (
    <section className="overflow-hidden border-y border-navy-600/60 py-8 md:py-10" data-testid="editorial-marquee">
      <div className="marquee-slow flex w-max items-center gap-10 whitespace-nowrap">
        {row.map((w, i) => (
          <span key={`${w}-${i}`} className="flex items-center gap-10">
            <span className={`font-heading text-5xl font-extrabold md:text-7xl ${i % 2 ? "text-outline" : "text-white/90"}`}>
              {w}
            </span>
            <span className="h-3 w-3 rotate-45 bg-brand" aria-hidden="true" />
          </span>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [showIntro, setShowIntro] = useState(() => {
    try {
      return !sessionStorage.getItem("et_intro_seen");
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!showIntro) return;
    try {
      sessionStorage.setItem("et_intro_seen", "1");
    } catch {}
    const t = setTimeout(() => setShowIntro(false), 1600);
    return () => clearTimeout(t);
  }, [showIntro]);

  const base = showIntro ? 1.1 : 0.05;

  return (
    <div data-testid="home-page">
      <AnimatePresence>{showIntro && <Intro key="intro" />}</AnimatePresence>

      <Hero base={base} />
      <EditorialMarquee />
      <UnixxStrip />
      <CompanyProfile />

      <section id="core-business" className="mx-auto max-w-7xl px-6 py-28 md:px-12" data-testid="core-business-section">
        <Chapter no="01" kicker="Core Business" title="Three product lines. One accountable counterparty." />
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {BUSINESSES.map((b, i) => (
            <motion.div key={b.title} {...reveal(i * 0.1)}>
              <Link
                to={b.to}
                data-testid={b.testid}
                className="spotlight-card group block overflow-hidden rounded-lg border border-navy-600 bg-navy-800 transition-colors duration-300 hover:border-brand/50"
              >
                <div className="clip-frame relative h-60 overflow-hidden">
                  <img
                    src={b.image}
                    alt={b.title}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                  <span className="absolute left-5 top-5 z-10 font-mono text-xs text-brand">/0{i + 1}</span>
                </div>
                <div className="relative z-10 p-7">
                  <h3 className="text-xl font-bold text-white">{b.title}</h3>
                  <ul className="mt-4 space-y-2 text-sm text-slate-400">
                    {b.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
                    View product line <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-navy-600/60 bg-navy-800/50" data-testid="why-us-section">
        <div className="mx-auto max-w-7xl px-6 py-28 md:px-12">
          <Chapter no="02" kicker="Why Choose Us" title="Built for buyers who cannot afford surprises" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <motion.div
                key={w.title}
                {...reveal(i * 0.08)}
                whileHover={{ y: -6 }}
                className="spotlight-card relative overflow-hidden rounded-lg border border-navy-600 bg-navy-800 p-7 transition-colors duration-300 hover:border-brand/50"
                data-testid={`why-card-${i}`}
              >
                <span className="text-outline-ghost absolute right-5 top-4 font-heading text-5xl font-extrabold">0{i + 1}</span>
                <w.icon className="relative z-10 h-8 w-8 text-brand" />
                <h3 className="relative z-10 mt-5 text-lg font-bold text-white">{w.title}</h3>
                <p className="relative z-10 mt-3 text-sm leading-relaxed text-slate-400">{w.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-28 md:px-12 lg:grid-cols-2" data-testid="markets-section">
        <motion.div {...reveal()}>
          <p className="font-mono text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-brand">03 — Our Markets</p>
          <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">Global connectivity from Singapore</h2>
          <p className="mt-5 text-base leading-relaxed text-slate-300">
            Operating from South-East Asia's logistics hub, we move cargoes efficiently between origins and
            destinations worldwide, serving clients across:
          </p>
          <ul className="mt-6 space-y-3 text-sm text-slate-300">
            <li className="flex items-center gap-3"><Globe2 className="h-4 w-4 text-brand" /> Southeast Asia — Singapore, Indonesia, Thailand, Timor-Leste</li>
            <li className="flex items-center gap-3"><Globe2 className="h-4 w-4 text-brand" /> South Asia</li>
            <li className="flex items-center gap-3"><Globe2 className="h-4 w-4 text-brand" /> Middle East</li>
          </ul>
        </motion.div>
        <motion.div {...reveal(0.1)} className="clip-frame overflow-hidden">
          <motion.img
            src="/assets/port.webp"
            alt="Container port operations in Singapore"
            className="h-full w-full object-cover"
            initial={{ scale: 1.15 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: EASE }}
          />
        </motion.div>
      </section>

      <section className="border-y border-navy-600/60 bg-navy-800/50" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-6 py-28 md:px-12">
          <Chapter no="04" kicker="How We Work" title="From enquiry to delivery in four steps" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                {...reveal(i * 0.08)}
                whileHover={{ y: -6 }}
                className="relative rounded-lg border border-navy-600 bg-navy-800 p-7 transition-colors duration-300 hover:border-brand/50"
                data-testid={`step-card-${i}`}
              >
                <span className="text-outline font-heading text-4xl font-extrabold">{s.n}</span>
                <h3 className="mt-4 text-lg font-bold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-28 md:px-12" data-testid="compliance-section">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div {...reveal()}>
            <p className="font-mono text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-brand">05 — Compliance &amp; Documentation</p>
            <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">Paperwork handled. Every shipment.</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-300">
              We support standard international trade documentation so your cargo clears smoothly at both ends.
            </p>
          </motion.div>
          <div className="grid gap-4 sm:grid-cols-2">
            {DOCS.map((d, i) => (
              <motion.div
                key={d}
                {...reveal(i * 0.06)}
                whileHover={{ y: -4 }}
                className="flex items-center gap-3 rounded-lg border border-navy-600 bg-navy-800 p-5 transition-colors duration-300 hover:border-brand/50"
                data-testid={`doc-card-${i}`}
              >
                <FileCheck2 className="h-6 w-6 shrink-0 text-brand" />
                <span className="text-sm font-medium text-slate-200">{d}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="quote" className="border-t border-navy-600/60 bg-navy-800/50" data-testid="home-quote-section">
        <div className="mx-auto max-w-4xl px-6 py-28 md:px-12">
          <motion.div {...reveal()} className="text-center">
            <p className="font-mono text-xs uppercase tracking-[0.2em] sm:tracking-[0.35em] text-brand">Request For Quote</p>
            <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">Tell us what you need to move</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-400">
              Share your specification, volume and destination — our trading desk responds with pricing and
              availability, typically within one business day.
            </p>
          </motion.div>
          <motion.div {...reveal(0.1)} className="mt-12">
            <EnquiryForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
